import { fetchSupplyToEarnReward } from "@/apis/lendingRequests"
import { appModalDisableState } from "@/atoms/appModalDisableState"
import { appBorrowRepayPopupState, appSupplyWithdrawPopupState } from "@/atoms/appPopupState"
import { selectedReserveAtom } from "@/atoms/modal"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { networkAtom } from "@/atoms/settings"
import { USDS_STE_REWARD, supplyToEarnAPYAtom, supplyToEarnRewardAtom } from "@/atoms/supplyToEarnState"
import { BORROW, DEPOSIT, IS_MAINNET, REPAY, WITHDRAW, isRunningInCypress } from "@/utils/constants"
import { ObligationReserveType } from "@/utils/dataTypes"
import { stringifyVariable } from "@/utils/helpers"
import { sendEventLog } from "@/utils/tracking-events"
import { SelectChangeEvent } from "@mui/material"
import { useRollbar } from "@rollbar/react"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "react-query"
import { toast } from "react-toastify"
import { useSessionStorage } from "usehooks-ts"
import { BORROW_HEADER_VALUES, SelectBorrowHeader } from "./types"

import { selectedSolendObligationAtom } from "@/atoms/solendObligations"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import View from "./view"

const Container = () => {
  const [tab, setTab] = useSessionStorage<typeof DEPOSIT | typeof BORROW>("mySupplyBorrowTab", DEPOSIT)
  const [selectedBorrowHeader, setSelectedBorrowHeader] = useState<string>(BORROW_HEADER_VALUES.apy)
  const { address, isSolanaChain } = useWalletConnect()
  const rollbar = useRollbar()
  const trans = useTrans()

  // atoms
  const setSupplyWithdrawPopupState = useSetAtom(appSupplyWithdrawPopupState)
  const setModalDisableState = useSetAtom(appModalDisableState)
  const setBorrowRepayPopupState = useSetAtom(appBorrowRepayPopupState)
  const setSelectedReserve = useSetAtom(selectedReserveAtom)
  const [selectedObligation] = useAtom(selectedMainObligationAtom)
  const [selectedSolendObligation] = useAtom(selectedSolendObligationAtom)
  const network = useAtomValue(networkAtom)

  const { data: supplyToEarnRewardData, refetch: refetchSTE } = useQuery(
    ["fetchSupplyToEarnReward", address, selectedObligation?.address],
    () => {
      return fetchSupplyToEarnReward(network, address, selectedObligation?.address)
    },
    {
      staleTime: 10000,
    },
  )
  useEffect(() => {
    if (isRunningInCypress) {
      refetchSTE()
    }
  }, [refetchSTE])

  const [supplyToEarnRewards, setSupplyToEarnRewards] = useAtom(supplyToEarnRewardAtom)
  const supplyToEarnApys = useAtomValue(supplyToEarnAPYAtom)

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newTab: typeof DEPOSIT | typeof BORROW) => {
      setTab(newTab)
      const eventName = newTab === DEPOSIT ? "supply" : "borrow"
      sendEventLog(`view_my_supply_borrow_${eventName}_tab`, {
        isMainnet: IS_MAINNET,
      })
    },
    [setTab],
  )

  const disableModal = useCallback(() => {
    setModalDisableState(true)

    setTimeout(() => {
      setModalDisableState(false)
    }, 1000)
  }, [setModalDisableState])

  const handleOpenSupplyWithdrawPopup = useCallback(
    (item: ObligationReserveType, type: typeof DEPOSIT | typeof WITHDRAW) => {
      setSupplyWithdrawPopupState({ isOpen: true, type })
      setSelectedReserve(item.reserveAddress)
      disableModal()

      if (type === DEPOSIT) {
        sendEventLog("view_supply_popup", {
          isMainnet: IS_MAINNET,
        })
      } else {
        sendEventLog("view_withdrawal_popup", {
          isMainnet: IS_MAINNET,
        })
      }
    },
    [disableModal, setSelectedReserve, setSupplyWithdrawPopupState],
  )

  const handleOpenBorrowRepayPopup = useCallback(
    (item: ObligationReserveType, type: typeof BORROW | typeof REPAY) => {
      setBorrowRepayPopupState({ isOpen: true, type })
      setSelectedReserve(item.reserveAddress)
      disableModal()

      if (type === BORROW) {
        sendEventLog("view_borrow_popup", {
          isMainnet: IS_MAINNET,
        })
      } else {
        sendEventLog("view_repay_popup", {
          isMainnet: IS_MAINNET,
        })
      }
    },
    [disableModal, setSelectedReserve, setBorrowRepayPopupState],
  )

  const borrowHeaderAprSelectItems: SelectBorrowHeader[] = useMemo(
    () => [
      {
        value: BORROW_HEADER_VALUES.apr,
        title: trans.dashboard.table.borrow_apr,
      },
      {
        value: BORROW_HEADER_VALUES.apy,
        title: trans.dashboard.table.borrow_apy,
      },
    ],
    [trans],
  )
  const handleChangeSelectHeader = useCallback(
    (event: SelectChangeEvent) => {
      const item =
        borrowHeaderAprSelectItems.find((i) => i.value === event.target.value) ?? borrowHeaderAprSelectItems[0]
      setSelectedBorrowHeader(item.value)
    },
    [borrowHeaderAprSelectItems],
  )

  const doesHaveSupply = isSolanaChain
    ? Boolean(selectedObligation?.deposits.length || selectedSolendObligation?.deposits.length)
    : Boolean(selectedObligation?.deposits.length)
  const doesHaveBorrow = Boolean(selectedObligation?.borrows.length)

  useEffect(() => {
    const newSupplyToEarnRewards = []
    if (IS_MAINNET && isSolanaChain) {
      newSupplyToEarnRewards.push(USDS_STE_REWARD)
    }
    if (supplyToEarnRewardData && !supplyToEarnRewardData.error) {
      newSupplyToEarnRewards.push(...supplyToEarnRewardData)
    } else if (supplyToEarnRewardData?.error) {
      toast.error(trans.dashboard.table.errors.fetch_ste_reward)
      rollbar.error(
        `RELEND: Error when fetching supply reward data. Wallet address: ${address}. Obligation Address: ${selectedObligation?.address}. Error: ${stringifyVariable(
          supplyToEarnRewardData.error,
        )}`,
      )
    }
    setSupplyToEarnRewards(newSupplyToEarnRewards)
  }, [rollbar, selectedObligation, setSupplyToEarnRewards, supplyToEarnRewardData, trans, address, isSolanaChain])

  return (
    <View
      tab={tab}
      handleTabChange={handleTabChange}
      doesHaveBorrow={doesHaveBorrow}
      doesHaveSupply={doesHaveSupply}
      supplyToEarnApys={supplyToEarnApys}
      supplyToEarnRewards={supplyToEarnRewards}
      handleOpenSupplyWithdrawPopup={handleOpenSupplyWithdrawPopup}
      selectedBorrowHeader={selectedBorrowHeader}
      borrowHeaderAprSelectItems={borrowHeaderAprSelectItems}
      handleChangeSelectHeader={handleChangeSelectHeader}
      handleOpenBorrowRepayPopup={handleOpenBorrowRepayPopup}
    />
  )
}

export default Container
